import {
  AttachSubsidiaryCommandDto,
  CreateSubsidiaryCommandDto,
  GetSubsidiariesResponse,
  LegalEntityFinancialsDto,
  SubsidiaryDto,
  UpdateLegalEntityFinancialsCommandDto,
  UpdateSubsidiaryCommandDto
} from 'api/actor';
import { toDayInput, toDayPayload } from 'core/utils/dates';
import { LegalEntityFinancialsFormValues } from 'features/new-actor/types/optim/legalEntityFinancialsFormValues';
import { SubsidiaryFormValues } from 'features/new-actor/types/optim/subsidiaryFormValues';

export interface SubsidiariesFormData {
  financials: LegalEntityFinancialsFormValues;
  subsidiaries: SubsidiaryFormValues[];
}

export const emptyFinancialsFormValues = (): LegalEntityFinancialsFormValues => ({
  revenue: '',
  employeesCount: '',
  fiscalYearEnd: ''
});

// Validation guarantees the numbers are filled before any command is built.
const toNumber = (value: number | ''): number => (value === '' ? 0 : Number(value));

// Calendar date: string slicing only, never Date / toISOString (shifts a day).
const toDate = (value: string): string => toDayPayload(value) ?? '';

const mapFinancialsDto = (dto?: LegalEntityFinancialsDto | null): LegalEntityFinancialsFormValues =>
  dto
    ? {
      revenue: dto.revenue,
      employeesCount: dto.employeesCount,
      fiscalYearEnd: toDayInput(dto.fiscalYearEnd)
    }
    : emptyFinancialsFormValues();

export const mapSubsidiaryDto = (dto: SubsidiaryDto): SubsidiaryFormValues => ({
  id: dto.id,
  companyName: dto.companyName,
  registrationNumber: dto.registrationNumber,
  revenue: dto.revenue,
  employeesCount: dto.employeesCount,
  fiscalYearEnd: toDayInput(dto.fiscalYearEnd),
  legalPersonId: dto.legalPersonId ?? null
});

export function mapSubsidiariesResponse(response: GetSubsidiariesResponse): SubsidiariesFormData {
  return {
    financials: mapFinancialsDto(response.parentFinancials),
    subsidiaries: response.subsidiaries.map(mapSubsidiaryDto)
  };
}

export function mapSubsidiaryToCreateCommand(values: SubsidiaryFormValues): CreateSubsidiaryCommandDto {
  return {
    companyName: values.companyName.trim(),
    registrationNumber: values.registrationNumber.trim(),
    revenue: toNumber(values.revenue),
    employeesCount: toNumber(values.employeesCount),
    fiscalYearEnd: toDate(values.fiscalYearEnd)
  };
}

export function mapSubsidiaryToUpdateCommand(values: SubsidiaryFormValues): UpdateSubsidiaryCommandDto {
  return mapSubsidiaryToCreateCommand(values);
}

export function mapSubsidiaryToAttachCommand(values: SubsidiaryFormValues): AttachSubsidiaryCommandDto {
  return {
    legalPersonId: values.legalPersonId ?? '',
    revenue: toNumber(values.revenue),
    employeesCount: toNumber(values.employeesCount),
    fiscalYearEnd: toDate(values.fiscalYearEnd)
  };
}

export function mapFinancialsToCommand(values: LegalEntityFinancialsFormValues): UpdateLegalEntityFinancialsCommandDto {
  return {
    revenue: toNumber(values.revenue),
    employeesCount: toNumber(values.employeesCount),
    fiscalYearEnd: toDate(values.fiscalYearEnd)
  };
}
